import { GameEvents } from '../GameEvents';
import type EventBus from '../EventBus';
import {
  trackLevelStarted,
  trackLevelFinished,
  trackLevelFailed,
  trackLevelAbandoned,
  trackLevelRestarted,
  trackHintViewed,
  trackEnemyRangeChecked,
  trackVisionViewed,
  trackSolutionReplayStarted,
  trackSolutionReplayExited,
} from '../analytics';

export default class AnalyticsHandler {
  private state: any;
  private eventBus: EventBus;

  constructor(state: any, eventBus: EventBus) {
    this.state = state;
    this.eventBus = eventBus;

    this.eventBus.on(GameEvents.LEVEL_WON, (payload: any) => this._onLevelWon(payload));
    this.eventBus.on(GameEvents.LEVEL_LOST, (payload: any) => this._onLevelLost(payload));
    this.eventBus.on(GameEvents.LEVEL_STARTED, (payload: any) => this._onLevelStarted(payload));
    this.eventBus.on(GameEvents.RESTART_GAME, () => this._onRestart());
    this.eventBus.on(GameEvents.SHOW_TUTORIAL, () => this._onShowTutorial());
    this.eventBus.on(GameEvents.RETURN_TO_HOME, () => this._onReturnToHome());
    this.eventBus.on(GameEvents.ENEMY_EXAMINED, (payload: any) => this._onEnemyExamined(payload));
    this.eventBus.on(GameEvents.SHOW_VISION, (payload: any) => this._onShowVision(payload));
    this.eventBus.on(GameEvents.START_SOLUTION_REPLAY, (payload: any) => this._onStartSolutionReplay(payload));
    this.eventBus.on(GameEvents.EXIT_SOLUTION_REPLAY, () => this._onExitSolutionReplay());
  }

  _onLevelWon(payload?: { moveCount: number; undoCount?: number }) {
    const levelKey = this.state.gameplay.currentLevelKey;
    if (!levelKey) return;
    const levelState = this.state.progress.levelStates[levelKey] || { failures: 0 };
    const attemptNumber = levelState.failures + 1;
    trackLevelFinished(levelKey, attemptNumber, payload?.moveCount ?? 0, payload?.undoCount ?? 0);
  }

  _onLevelLost(payload?: { moveCount: number }) {
    const levelKey = this.state.gameplay.currentLevelKey;
    if (!levelKey) return;
    const levelState = this.state.progress.levelStates[levelKey] || { failures: 0 };
    const attemptNumber = levelState.failures + 1;
    trackLevelFailed(levelKey, attemptNumber, payload?.moveCount ?? 0);
  }

  _onLevelStarted({ levelKey }: { levelKey: string }) {
    const levelState = this.state.progress.levelStates[levelKey] || { failures: 0 };
    const attemptNumber = levelState.failures + 1;
    trackLevelStarted(levelKey, attemptNumber);
  }

  _onRestart() {
    const levelKey = this.state.gameplay.currentLevelKey;
    if (!levelKey) return;
    const levelState = this.state.progress.levelStates[levelKey] || { failures: 0 };
    const attemptNumber = levelState.failures + 1;
    const moveCount = this.state.gameplay.currentMoveCount ?? 0;
    trackLevelRestarted(levelKey, attemptNumber, moveCount);
  }

  _onShowTutorial() {
    const levelKey = this.state.gameplay.currentLevelKey;
    if (!levelKey) return;
    const levelState = this.state.progress.levelStates[levelKey] || { failures: 0 };
    const attemptNumber = levelState.failures + 1;
    trackHintViewed(levelKey, attemptNumber, 'manual');
  }

  _onReturnToHome() {
    const levelKey = this.state.gameplay.currentLevelKey;
    // Only track abandonment if actively playing (not from home screen or win/lose screens)
    if (!levelKey || this.state.screen !== 'playing') return;
    const levelState = this.state.progress.levelStates[levelKey] || { failures: 0 };
    const attemptNumber = levelState.failures + 1;
    const moveCount = this.state.gameplay.currentMoveCount ?? 0;
    trackLevelAbandoned(levelKey, attemptNumber, moveCount);
  }

  _onEnemyExamined(payload: { entityId: number; enemyName: string }) {
    const levelKey = this.state.gameplay.currentLevelKey;
    if (!levelKey) return;
    trackEnemyRangeChecked(levelKey, payload?.enemyName);
  }

  _onShowVision({ levelKey }: { levelKey: string }) {
    const levelState = this.state.progress.levelStates[levelKey] || { failures: 0 };
    const attemptNumber = levelState.failures + 1;
    trackVisionViewed(levelKey, attemptNumber);
  }

  _onStartSolutionReplay(payload?: { source?: string }) {
    const levelKey = this.state.gameplay.currentLevelKey;
    if (!levelKey) return;
    // Vision also emits START_SOLUTION_REPLAY and is tracked separately via _onShowVision.
    // Only track explicit replay requests from the win screen.
    // NOTE: We check the payload source rather than this.state.screen because
    // SolutionReplayHandler (registered before us) synchronously triggers a screen
    // transition to 'playing' before our listener runs, so the screen check always fails.
    if (payload?.source !== 'win_screen') return;
    trackSolutionReplayStarted(levelKey);
  }

  _onExitSolutionReplay() {
    const levelKey = this.state.gameplay.currentLevelKey;
    if (!levelKey) return;
    const { currentStepIndex, totalSteps } = this.state.solutionReplay;
    trackSolutionReplayExited(levelKey, currentStepIndex, totalSteps);
  }
}
